import { useCallback, useEffect, useState } from "react";
import { getDistribution } from "../../../services/clearing/station-performance";
import type {
  DistributionReport,
  ReportingOptions,
} from "../../../types/stationPerformance";

interface UseStationDetailArgs {
  unitId: string | null;
  month: string;
  options: ReportingOptions;
  measure?: string;
}

/**
 * One station's detail: its month-by-month series and its within-month session spread.
 *
 * Two calls rather than one, because they answer different questions: ACROSS_MONTHS is "how does this
 * station move over time" and WITHIN_UNIT_SESSIONS is "what do its individual sessions look like".
 * They fail independently, so a spread that cannot be computed does not blank the time series.
 */
export const useStationDetail = ({
  unitId,
  month,
  options,
  measure = "moneyCents",
}: UseStationDetailArgs) => {
  const [acrossMonths, setAcrossMonths] = useState<DistributionReport | null>(null);
  const [withinMonth, setWithinMonth] = useState<DistributionReport | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!unitId) return;
    setInitialLoading(true);
    setError(null);

    // The variance endpoint refuses a provider filter, so it is dropped here rather than sent and
    // rejected: a per-channel spread cannot be derived from the stored counters.
    const varianceOptions = { ...options, provider: null };

    const [series, spread] = await Promise.all([
      getDistribution({ scope: "ACROSS_MONTHS", measure, unitId, options: varianceOptions }),
      getDistribution({
        scope: "WITHIN_UNIT_SESSIONS",
        measure: "energyKwh",
        month,
        unitId,
        options: varianceOptions,
      }),
    ]);

    setAcrossMonths(series?.success ? (series.data ?? null) : null);
    setWithinMonth(spread?.success ? (spread.data ?? null) : null);
    // Only surface an error when neither call produced anything; a partial view is still useful.
    if (!series?.success && !spread?.success) {
      setError(series?.message ?? spread?.message ?? "Could not load station detail.");
    }
    setInitialLoading(false);
  }, [unitId, month, options, measure]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return { acrossMonths, withinMonth, initialLoading, error, fetchDetail };
};
